<template>
  <div class="arp-wizard">
    <div class="arp-wizard__body">
      <ArpStepper :steps="stepperSteps" :active-key="activeStepperKey" />

      <div class="arp-wizard__content">
        <div class="arp-wizard__title-row">
          <div>
            <h2 class="title is-5 mb-1">{{ pageTitle }}</h2>
            <p v-if="pageSubtitle" class="has-text-grey is-size-7">{{ pageSubtitle }}</p>
          </div>
          <button
            v-if="showCancelar"
            type="button"
            class="button is-primary is-outlined"
            @click="onCancelar"
          >
            Cancelar
          </button>
        </div>

        <ArpChoice v-if="journey.step === 'choice' && !isEdit" @select="onSelectType" />
        <ArpFillVencedor v-else-if="journey.step === 'fill' && journey.type === 'vencedor'" />
        <ArpFillReserva v-else-if="journey.step === 'fill' && journey.type === 'reserva'" />
        <ArpFillPropria v-else-if="journey.step === 'fill' && journey.type === 'propria'" />
        <ArpSelectItens
          v-else-if="journey.step === 'itens' && journey.type === 'reserva'"
          :key="`arp-itens-${journey.type}-${itensReloadKey}`"
          mode="grouped"
          class="components"
        />
        <ArpReview v-else-if="journey.step === 'review'" />
        <ArpFeedback v-else-if="journey.step === 'done'" @fechar="onVoltarHub" />

        <div v-if="journey.step !== 'choice' && journey.step !== 'done'" class="arp-wizard__footer">
          <button type="button" class="button is-primary is-outlined" @click="onVoltar">
            Voltar
          </button>
          <button
            type="button"
            class="button is-primary"
            :class="{ 'is-loading': submitting }"
            :disabled="!canAvancar || submitting"
            @click="onAvancar"
          >
            {{ primaryLabel }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import ArpStepper from './components/ArpStepper';
import ArpSelectItens from './components/ArpSelectItens';
import ArpChoice from './steps/ArpChoice';
import ArpFillVencedor from './steps/ArpFillVencedor';
import ArpFillReserva from './steps/ArpFillReserva';
import ArpFillPropria from './steps/ArpFillPropria';
import ArpReview from './steps/ArpReview';
import ArpFeedback from './steps/ArpFeedback';
import { isAnoArpValido, isVigenciaArpDentroDoLimite } from './helpers';

const STEPS_BY_TYPE = {
  vencedor: [
    {
      key: 'choice',
      title: 'O que você quer fazer?',
      subtitle: 'Como registrar a ata neste certame',
    },
    {
      key: 'fill',
      title: 'Informar dados',
      subtitle: 'Preencha as informações da ata',
    },
    {
      key: 'review',
      title: 'Revisão',
      subtitle: 'Confira os dados antes de concluir',
    },
    {
      key: 'done',
      title: 'Concluído',
      subtitle: 'Concluído com Sucesso',
    },
  ],
  reserva: [
    {
      key: 'choice',
      title: 'O que você quer fazer?',
      subtitle: 'Como registrar a ata neste certame',
    },
    {
      key: 'fill',
      title: 'Informar dados',
      subtitle: 'Preencha as informações da ata',
    },
    {
      key: 'itens',
      title: 'Selecionar itens',
      subtitle: 'Escolha os itens da ata',
    },
    {
      key: 'review',
      title: 'Revisão',
      subtitle: 'Confira os dados antes de concluir',
    },
    {
      key: 'done',
      title: 'Concluído',
      subtitle: 'Concluído com Sucesso',
    },
  ],
  propria: [
    {
      key: 'choice',
      title: 'O que você quer fazer?',
      subtitle: 'Como registrar a ata neste certame',
    },
    {
      key: 'fill',
      title: 'Informar dados',
      subtitle: 'Preencha as informações da ata',
    },
    {
      key: 'review',
      title: 'Revisão',
      subtitle: 'Confira os dados antes de concluir',
    },
    {
      key: 'done',
      title: 'Concluído',
      subtitle: 'Concluído com Sucesso',
    },
  ],
};

function stepsForJourney(type, { isEdit, isRascunho }) {
  const base = STEPS_BY_TYPE[type] || STEPS_BY_TYPE.vencedor;
  let steps = base;
  if (isEdit) {
    steps = steps.filter(s => s.key !== 'choice');
  }
  // Retificação (publicada): metadados + justificativa — sem reescolher itens.
  if (isEdit && !isRascunho) {
    steps = steps.filter(s => s.key !== 'itens');
  }
  return steps;
}

export default {
  name: 'ArpWizard',
  components: {
    ArpStepper,
    ArpSelectItens,
    ArpChoice,
    ArpFillVencedor,
    ArpFillReserva,
    ArpFillPropria,
    ArpReview,
    ArpFeedback,
  },
  data() {
    return {
      submitting: false,
      itensReloadKey: 0,
    };
  },
  computed: {
    ...mapGetters({
      journey: 'obterArpJourney',
      draft: 'obterArpDraft',
    }),
    isEdit() {
      return this.journey.mode === 'edit';
    },
    isRascunho() {
      return this.journey.isRascunho !== false;
    },
    stepperSteps() {
      if (!this.journey.type) {
        return STEPS_BY_TYPE.vencedor;
      }
      return stepsForJourney(this.journey.type, {
        isEdit: this.isEdit,
        isRascunho: this.isRascunho,
      });
    },
    stepOrder() {
      return this.stepperSteps.map(s => s.key);
    },
    activeStepperKey() {
      return this.journey.step === 'choice' ? 'choice' : this.journey.step;
    },
    pageTitle() {
      if (this.journey.step === 'choice') return 'O que você quer fazer?';
      if (this.journey.step === 'review') return 'Revisão';
      if (this.journey.step === 'itens') return 'Selecionar itens';
      if (this.journey.step === 'done') return 'Concluído';
      if (this.isEdit) {
        if (this.journey.type === 'vencedor') return 'Editar ARP de Vencedor';
        if (this.journey.type === 'reserva') return 'Editar ARP Cadastro Reserva';
        if (this.journey.type === 'propria') return 'Editar ARP Própria';
        return 'Editar ARP';
      }
      if (this.journey.type === 'vencedor') return 'Gerar ARP para um Vencedor';
      if (this.journey.type === 'reserva') return 'Gerar ARP Cadastro Reserva';
      if (this.journey.type === 'propria') return 'Enviar ARP Própria';
      return 'Nova Ata';
    },
    pageSubtitle() {
      if (this.journey.step === 'choice') {
        return 'Escolha como deseja registrar a ata neste certame.';
      }
      if (this.journey.step === 'review') {
        if (this.isEdit && !this.isRascunho) {
          return 'Confira os dados e informe a justificativa da retificação.';
        }
        return 'Confira os dados antes de concluir';
      }
      if (this.journey.step === 'itens') {
        return this.journey.type === 'reserva'
          ? 'Selecione itens homologados dos fornecedores vencedores (diferentes do licitante convocado).'
          : 'Selecione os itens que farão parte da ARP.';
      }
      if (this.journey.step === 'done') return null;
      if (this.isEdit && !this.isRascunho) {
        return 'Altere os metadados da ata publicada (retificação PNCP).';
      }
      if (this.isEdit) {
        return 'Atualize as informações e regenere o rascunho da ata.';
      }
      return 'Preencha as informações da ata';
    },
    primaryLabel() {
      if (this.journey.step === 'review') {
        if (this.isEdit && !this.isRascunho) return 'Retificar';
        if (this.isEdit) return 'Salvar';
        return 'Confirmar';
      }
      return 'Continuar';
    },
    showCancelar() {
      return this.journey.step !== 'done';
    },
    canAvancar() {
      if (this.journey.step === 'fill') return this.isFillValid;
      if (this.journey.step === 'itens') {
        return (this.draft.itensSelecionados || []).length > 0;
      }
      if (this.journey.step === 'review') {
        if (this.isEdit && !this.isRascunho) {
          const j = String(this.draft.justificativa || '').trim();
          return j.length >= 10 && j.length <= 255 && !!this.draft.dataAssinatura;
        }
        return true;
      }
      return false;
    },
    isFillValid() {
      const d = this.draft;
      if (!d.numero || !isAnoArpValido(d.ano) || !d.vigenciaInicio || !d.vigenciaFim) return false;
      if (!isVigenciaArpDentroDoLimite(d.vigenciaInicio, d.vigenciaFim, 12)) return false;
      if (
        (this.journey.type === 'vencedor' || this.journey.type === 'reserva') &&
        !d.fornecedorId
      ) {
        return false;
      }
      if (this.journey.type === 'propria') {
        if (this.isEdit && this.isRascunho) {
          // Em edição de rascunho própria, mantém PDF já enviado.
          return true;
        }
        if (this.isEdit && !this.isRascunho) {
          return !!d.dataAssinatura;
        }
        if (!d.dataAssinatura || !d.arquivo) return false;
      }
      if (this.isEdit && !this.isRascunho && !d.dataAssinatura) {
        return false;
      }
      return true;
    },
  },
  methods: {
    ...mapActions([
      'atualizarArpJourney',
      'cancelarArpJourney',
      'concluirArpJourney',
      'carregarArpItensSelecao',
    ]),
    onSelectType(type) {
      this.atualizarArpJourney({ type, step: 'fill' });
    },
    onCancelar() {
      if (this.journey.step === 'choice') {
        this.cancelarArpJourney();
        return;
      }
      const mensagem = this.isEdit
        ? 'Deseja cancelar a edição desta ARP? As alterações não salvas serão perdidas.'
        : 'Deseja cancelar a criação desta ARP? Os dados preenchidos serão perdidos.';
      this.$confirmacao({
        message: mensagem,
        cancelText: 'Continuar editando',
        confirmText: 'Sim, cancelar',
        type: 'is-danger',
        hasIcon: true,
      }).then(confirmar => {
        if (confirmar) this.cancelarArpJourney();
      });
    },
    onVoltar() {
      const { step } = this.journey;
      const order = this.stepOrder;
      const idx = order.indexOf(step);
      if (idx <= 0) {
        if (this.isEdit) {
          this.cancelarArpJourney();
          return;
        }
        this.atualizarArpJourney({ type: null, step: 'choice' });
        return;
      }
      this.atualizarArpJourney({ step: order[idx - 1] });
    },
    async onAvancar() {
      const { step } = this.journey;
      const order = this.stepOrder;
      const idx = order.indexOf(step);

      if (step === 'review') {
        this.submitting = true;
        try {
          await this.concluirArpJourney();
        } catch (e) {
          const msg =
            e?.response?.data?.error ||
            e?.response?.data?.message ||
            e.message ||
            'Erro ao concluir';
          this.$alerta(msg, 'is-danger');
        } finally {
          this.submitting = false;
        }
        return;
      }

      if (idx >= 0 && idx < order.length - 1) {
        const nextStep = order[idx + 1];
        if (nextStep === 'itens') {
          this.itensReloadKey += 1;
          try {
            await this.carregarArpItensSelecao();
          } catch (e) {
            this.$alerta('Não foi possível carregar os itens da sessão.', 'is-danger');
            return;
          }
        }
        this.atualizarArpJourney({ step: nextStep });
      }
    },
    onVoltarHub() {
      this.cancelarArpJourney();
      this.$emit('voltar-hub');
    },
  },
};
</script>

<style scoped>
.arp-wizard__body {
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
}

.arp-wizard__content {
  flex: 1;
  min-width: 0;
}

.arp-wizard__title-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #dbdbdb;
}

.arp-wizard__title-row .button {
  flex-shrink: 0;
}

.arp-wizard__footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #dbdbdb;
}

@media (max-width: 768px) {
  .arp-wizard__body {
    flex-direction: column;
  }
}
</style>
